import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Calendar, User, ArrowLeft, Share2 } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import SEO from '../components/SEO';
import { useSiteData } from '../lib/SiteDataContext';
import { resolveImage } from '../lib/api';

const NewsDetailPage = () => {
  const { id } = useParams();
  const { news } = useSiteData();
  const item = (news || []).find((n) => n.id === id) || (news || [])[0];
  const related = (news || []).filter((n) => item && n.id !== item.id).slice(0, 3);

  useEffect(() => { window.scrollTo(0, 0); }, [id]);

  if (!item) {
    return (
      <section className="py-24 text-center">
        <h2 className="font-serif text-2xl font-bold text-[#0B2545]">No news available</h2>
      </section>
    );
  }

  return (
    <>
      <SEO
        title={item.title}
        description={(item.excerpt || item.body || '').slice(0, 200)}
        image={item.image_url ? resolveImage(item.image_url) : undefined}
        type="article"
        keywords={`${item.category}, Somalia news, South East State, ${item.title}`}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'NewsArticle',
          headline: item.title,
          datePublished: item.date,
          author: { '@type': 'Organization', name: item.author },
          image: item.image_url ? resolveImage(item.image_url) : undefined,
          articleSection: item.category,
          description: item.excerpt,
        }}
      />
      <PageHeader title={item.title} crumbs={[{ label: 'News', to: '/news' }, { label: 'Article' }]} />
      <article className="bg-white py-14">
        <div className="mx-auto max-w-[920px] px-6">
          <div className="mb-6 flex flex-wrap items-center gap-4 text-[12.5px] text-[#0B2545]/70">
            <span className="inline-flex items-center gap-1.5"><Calendar size={13} className="text-[#C9A86A]" /> {item.date}</span>
            <span className="inline-flex items-center gap-1.5"><User size={13} className="text-[#C9A86A]" /> {item.author}</span>
            <span className="rounded-full bg-[#F6F2EA] px-2.5 py-0.5 text-[11px] font-medium uppercase tracking-wide text-[#C9A86A]">{item.category}</span>
          </div>

          {item.image_url && (
            <div className="mb-8 overflow-hidden rounded-lg border border-[#E6DDC8]">
              <img src={resolveImage(item.image_url)} alt={item.title} className="h-auto max-h-[480px] w-full object-cover" />
            </div>
          )}

          <div className="prose prose-lg max-w-none">
            <p className="text-[16px] leading-relaxed text-[#0B2545]/85">{item.excerpt}</p>
            <p className="mt-5 whitespace-pre-line text-[15px] leading-relaxed text-[#0B2545]/80">{item.body}</p>
          </div>

          <div className="mt-10 flex items-center justify-between border-t border-[#E6DDC8] pt-6">
            <Link to="/news" className="inline-flex items-center gap-2 text-[14px] font-medium text-[#0B2545] hover:text-[#C9A86A]">
              <ArrowLeft size={16} /> Back to News
            </Link>
            <button
              onClick={() => navigator.clipboard?.writeText(window.location.href)}
              className="inline-flex items-center gap-2 rounded-md border border-[#E6DDC8] px-4 py-2 text-[13px] font-medium text-[#0B2545] hover:bg-[#F6F2EA]"
            >
              <Share2 size={14} /> Copy link
            </button>
          </div>
        </div>
      </article>

      {related.length > 0 && (
        <section className="bg-[#FAF6EC] py-16">
          <div className="mx-auto max-w-[1280px] px-6">
            <h3 className="mb-8 font-serif text-2xl font-bold text-[#0B2545]">Related News</h3>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              {related.map((n) => (
                <Link key={n.id} to={`/news/${n.id}`} className="group overflow-hidden rounded-lg border border-[#E6DDC8] bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-lg">
                  <div className="h-[170px] overflow-hidden">
                    {n.image_url && (
                      <img src={resolveImage(n.image_url)} alt={n.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    )}
                  </div>
                  <div className="p-5">
                    <div className="text-[11.5px] uppercase tracking-wide text-[#C9A86A]">{n.date}</div>
                    <h4 className="mt-2 font-serif text-[16px] font-semibold leading-snug text-[#0B2545] group-hover:text-[#C9A86A]">{n.title}</h4>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
};

export default NewsDetailPage;
